import {
  computed,
  inject,
  Injectable,
  INJECTOR,
  linkedSignal,
  LOCALE_ID,
  resource,
  Signal,
  signal,
} from '@angular/core';
import {
  ApiDataAccess,
  DateTimeService,
  Dictionary,
  formatDate,
  ResponseResult,
  Store,
} from '@softline/core';
import {
  SOFTLINE_FEATURE_CHECKLISTEN,
  SOFTLINE_FEATURE_CHECKPUNKTE,
} from '../checkliste.shared';
import { ChecklistenStore } from '../store/checklisten.store';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { Checkpunkt } from '../types/checkpunkt';
import {
  MessageBarStore,
  ModalStore,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  SOFTLINE_FEATURE_MODAL,
} from '@softline/ui-core';
import { SelectChecklisteDialogComponent } from '../dialogs/select-checkliste-dialog/select-checkliste-dialog.component';
import { handleRequestError, showRequestErrors } from '@softline/application';
import { CreateChecklisteDialogComponent } from '../dialogs/create-checkliste-dialog/create-checkliste-dialog.component';
import { CheckpunkteStore } from '../store/checkpunkte.store';
import { TaskRepository } from '../repositories/task.repository';
import { SOFTLINE_API_CHECKLISTE_ENTITIES } from '../checkliste.api';
import { Checkliste } from '../types/checkliste';

export interface LoadChecklistParams {
  from?: string;
  to?: string;
  all?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ChecklistenService {
  readonly #dataAccess = inject(ApiDataAccess);
  readonly #injector = inject(INJECTOR);
  readonly #localeId = inject(LOCALE_ID);

  readonly dateService = inject(DateTimeService);
  readonly taskRepository = inject(TaskRepository);

  readonly paramsSource = signal<Signal<LoadChecklistParams | null>>(signal(null));

  readonly #params = linkedSignal(() => {
    const source = this.paramsSource();
    return source ? source() : null;
  });

  readonly params = computed(() => {
    const params = this.#params();
    return params;
  });

  readonly #resource = resource({
    params: () => this.params(),
    loader: async (params) => {
      const response = await this.#dataAccess.get<ResponseResult<Checkliste[]>>({
        path: SOFTLINE_API_CHECKLISTE_ENTITIES,
        queryParams: params?.params ? (params.params as Dictionary<unknown>) : undefined,
      });

      return response.result;
    },
  });

  readonly resource = this.#resource.asReadonly();

  readonly checklisten = computed(() => {
    return this.resource.value() ?? [];
  });

  constructor(private readonly store: Store) {}

  async loadMany(params?: LoadChecklistParams): Promise<void> {
    this.#params.set(params ?? this.paramsSource()() ?? null);
  }

  trackParams(externalParams: Signal<LoadChecklistParams | null>): void {
    this.paramsSource.set(externalParams);
  }

  async createCheckpunkt(params: {
    idaufgabe: number;
    checkpunkt: string;
  }): Promise<Checkpunkt> {
    return await this.store.dispatch(
      SOFTLINE_FEATURE_CHECKPUNKTE,
      CheckpunkteStore.actions.create,
      {
        entity: { checkpunkt: params.checkpunkt },
        pathParams: { idaufgabe: params.idaufgabe },
      }
    );
  }

  async createCheckliste(): Promise<void> {
    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open<{ betreff: string } | null, object>(),
      {
        id: 'SOFTAPPS_ALLGEMEIN_CHECKLISTEN_ADD',
        dismiss: true,
        data: {},
        component: CreateChecklisteDialogComponent,
      }
    );

    if (!result || result === 'DISMISSED' || !result?.betreff) return;

    try {
      const checklist = await this.taskRepository.create(result.betreff);
      this.#resource.set([...(this.resource.value() ?? []), checklist]);
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        '#CHECKLISTE.DIALOGS.CREATE.SUCCESS'
      );
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
    }
  }

  async addToCheckliste(checkpunkt: Checkpunkt): Promise<void> {
    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open(),
      {
        id: 'SOFTAPPS_ALLGEMEIN_CHECKLISTEN_ADD',
        dismiss: true,
        data: { checkpunkt },
        component: SelectChecklisteDialogComponent,
      }
    );

    if (!result || result === 'DISMISSED') return;

    try {
      // store.dispatch goes here (add to checkliste)
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }
}
